import { 
    Tag, 
    Sparkles, 
    Globe, 
    Map,
    Zap
} from 'lucide-react'
import { PublicPlan, TopUpPlan } from '@/types/esim'


export interface PublicRegion {
  region_code: string
  countries: { iso2: string; name: string }[]
}

export interface PublicCountry {
  country: string
  iso2: string
}

export function getPromoInfo(plan: PublicPlan) {
  const original = plan.retail_price_converted_guest
  const discounted = plan.retail_price_converted_after_offer
  const hasPromo = typeof original === 'number' && discounted < original
  const savings = hasPromo ? parseFloat((original - discounted).toFixed(2)) : 0
  const discountPct = hasPromo ? Math.round(((original - discounted) / original) * 100) : 0
  return { hasPromo, original, discounted, savings, discountPct }
}

export function getPrivatePromoInfo(plan: TopUpPlan) {
  const original = plan.retail_price_converted
  const discounted = plan.retail_price_converted_after_offer
  const hasPromo = typeof original === 'number' && discounted < original
  const savings = hasPromo ? parseFloat((original - discounted).toFixed(2)) : 0
  const discountPct = hasPromo ? Math.round(((original - discounted) / original) * 100) : 0
  return { hasPromo, original, discounted, savings, discountPct }
}

export function PromoBadge({ discountPct }: { discountPct: number }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 global-border-radius bg-brand-green/15 border border-brand-green/25 text-brand-green text-xs font-bold uppercase tracking-wider 3xl:text-sm">
      <Tag className="h-2.5 w-2.5" />
      -{discountPct}%
    </span>
  )
}

export function FirstOfferBanner() {
  return (
    <div className="flex items-center gap-3 px-4 py-3 global-border-radius border border-brand-green/20 bg-brand-green/5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center global-border-radius bg-brand-green/15">
        <Sparkles className="h-4 w-4 text-brand-green" />
      </div>
      <div>
        <p className="text-xs font-bold text-brand-green 3xl:text-sm">First eSIM offer active</p>
        <p className="text-xs text-brand-muted 3xl:text-sm">Discounted prices shown for your first eSIM purchase</p>
      </div>
    </div>
  )
}

export function PriceDisplay({
  plan,
  size = 'md',
  align = 'end',
}: {
  plan: PublicPlan
  size?: 'sm' | 'md' | 'lg'
  align?: 'start' | 'end'
}) {
  const { hasPromo, original, discounted, discountPct } = getPromoInfo(plan)

  if (!hasPromo) {
    return (
      <span
        className={
          size === 'lg'
            ? 'text-xl font-bold text-brand-text 3xl:text-2xl'
            : size === 'sm'
            ? 'text-xs font-bold text-brand-text 3xl:text-sm'
            : 'text-sm font-bold text-brand-text 3xl:text-xl'
        }
      >
        ${discounted.toFixed(2)}
      </span>
    )
  }

  return (
    <div className={`flex items-center justify-center gap-0.5`}>
      <span className="text-xs text-brand-muted line-through tabular-nums 3xl:text-base">
          ${original.toFixed(2)}
      </span>
      <span
        className={`font-bold text-brand-text tabular-nums ${
          size === 'lg' ? 'text-xl 3xl:text-2xl' : size === 'sm' ? 'text-xs 3xl:text-sm' : 'text-base 3xl:text-xl'
        }`}
      >
        ${discounted.toFixed(2)} AUD
      </span>
    </div>
  )
}

export function PlanTypeBadge({ type }: { type: 'fixedData' | 'unlimitedStandard' }) {
  return type === 'unlimitedStandard' ? (
    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 global-border-radius 3xl:text-sm bg-brand-purple/10 border border-brand-purple/20 text-brand-purple">
      <Zap className="h-2.5 w-2.5" />
      Unlimited
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 global-border-radius 3xl:text-sm bg-brand-teal/10 border border-brand-teal/20 text-brand-teal">
      Fixed
    </span>
  )
}

export function CoverageBadge({ type }: { type: 'country' | 'region' }) {
  return type === 'region' ? (
    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 global-border-radius 3xl:text-sm bg-brand-purple/10 border border-brand-purple/20 text-brand-purple">
      <Map className="h-2.5 w-2.5" />
      Region
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 global-border-radius 3xl:text-sm bg-brand-teal/10 border border-brand-teal/20 text-brand-teal">
      <Globe className="h-2.5 w-2.5" />
      Country
    </span>
  )
}


